import React from 'react';
import { Link } from 'react-router-dom';
import MiniContactForm from '../components/MiniContactForm';
import FAQ from '../components/FAQ';
import SEO from '../components/SEO';
import { BlogTilePreview } from '../components/Blog';
import { BLOG_POSTS } from '../components/Blog';
import BlogTile from '../components/BlogTile';
import ServiceBlogSection from '../components/ServiceBlogSection';

const DRIVEWAY_FAQ = [
  {
    question: 'How often should I have my driveway cleaned?',
    answer: 'We recommend cleaning your driveway at least once a year to maintain its appearance and prevent buildup of dirt, oil stains, and algae. In areas with heavy traffic or frequent spills, more frequent cleaning may be necessary.'
  },
  {
    question: 'Can you remove oil stains from my driveway?',
    answer: 'Yes! We use specialized degreasers and cleaning solutions to effectively remove oil stains from driveways. Our process involves pre-treating the stains with eco-friendly solutions before pressure washing, ensuring the best possible results.'
  },
  {
    question: 'Is pressure washing safe for all types of driveways?',
    answer: {
      intro: 'We adjust our cleaning methods based on your driveway material:',
      list: [
        'Concrete driveways: We use appropriate pressure settings and cleaning solutions to avoid surface damage.',
        'Asphalt driveways: We use lower pressure settings to prevent surface erosion.',
        'Paver driveways: We carefully clean between pavers and can re-sand the joints after cleaning.'
      ],
      outro: 'Our experienced technicians will assess your driveway and use the most appropriate cleaning method for your specific surface.'
    }
  }
];

const DrivewayCleaning = () => {
  const serviceSEO = {
    title: 'Driveway Cleaning Services | Copper Canyon Pressure Wash',
    description: 'Professional driveway cleaning in Phoenix, AZ. Remove oil stains, dirt & grime. Expert technicians, eco-friendly solutions. Get your free quote today!'
  };
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Driveway Cleaning',
    'provider': {
      '@type': 'LocalBusiness',
      'name': 'Copper Canyon Pressure Wash'
    },
    'areaServed': {
      '@type': 'City',
      'name': ['Phoenix', 'Scottsdale', 'Mesa', 'Tempe', 'Gilbert', 'Paradise Valley']
    },
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Pressure Washing Services',
      'itemListElement': {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Driveway Cleaning'
        }
      }
    }
  };
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO 
        title="Driveway Cleaning Services | Copper Canyon Pressure Wash"
        description="Professional driveway cleaning in Phoenix, AZ. Remove oil stains, dirt & grime. Expert technicians, eco-friendly solutions. Get your free quote today!"
        schema={serviceSchema}
        keywords="concrete driveway cleaning, asphalt driveway cleaning, oil stain removal, Phoenix driveway restoration, driveway pressure washing, driveway sealing preparation, curb appeal enhancement, driveway maintenance, driveway surface cleaning, driveway safety cleaning"
      />
      <section className="w-full h-48 sm:h-64 md:h-80 lg:h-96 mb-8 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center" aria-label="Service Banner">
        <picture className="w-full h-full">
          <source srcSet="/Hero_Image.avif" type="image/avif" />
          <source srcSet="/Hero_Image.webp" type="image/webp" />
          <img 
            src="/Hero_Image.JPG" 
            alt="Professional driveway cleaning service in action" 
            className="w-full h-full object-cover object-center" 
            width="1600"
            height="400"
          />
        </picture>
      </section>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold text-[#4a3f35] mb-6">Driveway Cleaning</h1>
        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4">Professional Driveway Cleaning Services</h2>
        <p className="mb-6 text-gray-700">
          At Copper Canyon Pressure Wash, we specialize in professional driveway cleaning services throughout the greater Phoenix area, operating from our base in Scottsdale. Arizona's desert environment—think scorching heat, dust storms, and hard water stains—can leave your driveway stained and worn. Our team uses high-powered, gas-driven pressure washers with adjustable nozzles to restore your driveway's appearance, tackling everything from oil stains to embedded dirt, so your property makes a great first impression.
        </p>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Hot Water Power</h3>
            </div>
            <p className="text-gray-700">We use hot water pressure washing at up to 3,000 PSI to remove tough oil and grease stains that cold water can't touch.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Material Expertise</h3>
            </div>
            <p className="text-gray-700">We're experts at handling Arizona's common driveway materials, like stained concrete and pavers, ensuring a thorough clean without damage.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Longevity Boost</h3>
            </div>
            <p className="text-gray-700">Our cleaning process can extend your driveway's lifespan by removing corrosive mineral deposits caused by Phoenix's hard water.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Reliable Service</h3>
            </div>
            <p className="text-gray-700">We deliver reliable, on-time driveway cleaning services, keeping property looking pristine.</p>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4 mt-8">Our Driveway Cleaning Process</h2>
        <div className="bg-[#f8f7f3] rounded-lg p-6 mb-8">
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">1</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Detailed Inspection</h3>
                <p className="text-gray-700">We begin with a detailed inspection to identify stains like oil, tire marks, or desert grime on your driveway.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">2</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Biodegradable Pre-Treatment</h3>
                <p className="text-gray-700">We apply biodegradable detergents to break down grease and stains, ensuring a safe clean for your surrounding landscaping.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">3</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Precision Cleaning</h3>
                <p className="text-gray-700">Using our gas-driven pressure washers, we clean with hot water for stubborn stains and adjust pressure for a safe, even finish.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">4</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Final Rinse & Inspection</h3>
                <p className="text-gray-700">We conclude with a final rinse and inspection, leaving your driveway spotless and ready to impress.</p>
              </div>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4 mt-8">Our Expertise in Driveway Cleaning</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Surface-Specific Pressure</h3>
            <p className="text-gray-700">We choose the right pressure settings—like 2,500 PSI for concrete or 1,500 PSI for pavers—to prevent cracking or surface damage.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">All Driveway Types</h3>
            <p className="text-gray-700">We handle all driveway types, including sealed concrete, exposed aggregate, and interlocking pavers, with tailored techniques for each.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Damage Prevention</h3>
            <p className="text-gray-700">Our expertise prevents costly mistakes—like chipped sealants or eroded joints—that inexperienced teams might cause.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Best Practices</h3>
            <p className="text-gray-700">We stay informed on the best driveway cleaning methods, ensuring a safe, effective clean for Arizona's harsh desert conditions.</p>
          </div>
        </div>
      </div>

      {/* Call-to-Action Box */}
      <div className="max-w-3xl mx-auto mt-12">
        <div className="bg-[#f8f7f3] border-l-4 border-[#b87c4c] p-6 rounded-lg shadow mb-8">
          <h3 className="text-lg font-bold text-[#b87c4c] mb-2">Get a Free Quote for Driveway Cleaning!</h3>
          <p className="mb-4 text-gray-700">Contact us today for a special offer on our driveway cleaning services.</p>
          <Link 
            to="/contact" 
            className="inline-block px-6 py-2 bg-[#8B4513] text-white font-medium rounded-lg shadow hover:bg-[#6B3410] transition-colors duration-200"
          >
            Get a Free Quote
          </Link>
        </div>
      </div>

      <div className="max-w-2xl mx-auto mt-12 mb-12">
        <MiniContactForm />
      </div>

      <FAQ items={DRIVEWAY_FAQ} title="Frequently Asked Questions About Driveway Cleaning" />

      {/* Tips & Resources Blog Section */}
      <ServiceBlogSection blogSlugs={['driveway-cleaning-guide']} />
    </div>
  );
};

export default DrivewayCleaning; 